import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { workData } from "../Data.js";
import "./my-portfolio.scss";

/**
 * MyPortfolio Component
 * 
 * A single long vertical scrolling page that displays all project steps
 * as individual sections. Each section animates in when it enters the viewport.
 * 
 * Structure:
 * - Maps over all projects from workData
 * - For each project, maps over all steps
 * - Each step renders as a section with left (text) and right (image) layout
 * - Uses Intersection Observer for scroll-based reveal animations
 * - Handles hash navigation to scroll to specific projects
 */
export default function MyPortfolio() {
  const sectionRefs = useRef([]);
  const projectRefs = useRef({});
  const location = useLocation();

  // Handle hash navigation to scroll to specific project
  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (hash && projectRefs.current[hash]) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        projectRefs.current[hash].scrollIntoView({ 
          behavior: "smooth",
          block: "start"
        });
      }, 100);
    }
  }, [location.hash]);

  useEffect(() => {
    // Capture refs at effect run time so cleanup uses the same list (avoids exhaustive-deps warning)
    const refs = sectionRefs.current;

    // Intersection Observer for scroll-based animations
    const observerOptions = {
      root: null,
      rootMargin: "0px 0px -100px 0px", // Trigger slightly before section enters viewport
      threshold: 0.15, // Trigger when 15% of section is visible
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("visible");
          // Keep observing to allow re-animation if needed
        } else {
          // Optional: remove visible class when scrolling back up for re-animation
          // entry.target.classList.remove("visible");
        }
      });
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions
    );

    // Observe all sections
    refs.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      // Cleanup observer (use captured refs, not sectionRefs.current)
      refs.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  // Flatten all projects and steps into a single array for rendering
  const allSteps = [];
  workData.forEach((project) => {
    project.steps.forEach((step, stepIndex) => {
      allSteps.push({
        ...step,
        projectTitle: project.title,
        projectId: project.id,
        stepNumber: stepIndex + 1,
        totalSteps: project.steps.length,
      });
    });
  });

  return (
    <section className="my-portfolio" id="my-portfolio">
      <div className="portfolio-container">
        {allSteps.map((step, index) => {
          // Alternate layout: even index = text left, image right; odd index = image left, text right
          const isAlternate = index % 2 === 1;
          // Check if this is the first page of a project
          const isFirstPage = step.stepNumber === 1;
          
          return (
            <div
              key={`${step.projectId}-${step.stepNumber}`}
              id={isFirstPage ? step.projectId : undefined}
              ref={(el) => {
                sectionRefs.current[index] = el;
                // Store reference to first page of each project for hash navigation
                if (isFirstPage && el) {
                  projectRefs.current[step.projectId] = el;
                }
              }}
              className={`portfolio-section ${isAlternate ? "alternate" : ""} ${isFirstPage ? "first-page" : ""}`}
            >
              <div className="section-wrapper">
                {/* Text content */}
                <div className={`section-left ${isAlternate ? "order-2" : "order-1"}`}>
                  <div className="content">
                    {isFirstPage && (
                      <div className="project-title">{step.projectTitle}</div>
                    )}
                    <span className="stepCount">
                      {String(step.stepNumber).padStart(2, "0")} /{" "}
                      {String(step.totalSteps).padStart(2, "0")}
                    </span>

                    <h2 className={isFirstPage ? "first-page-title" : ""}>{step.title}</h2>

                    <p className="mainDesc">{step.desc}</p>

                    {step.highlight && (
                      <p className="highlight">{step.highlight}</p>
                    )}
                  </div>
                </div>

                {/* Image(s) */}
                <div className={`section-right ${isAlternate ? "order-1" : "order-2"}`}>
                  {step.img && (
                    <div className={Array.isArray(step.img) ? "imageContainer imageContainer--multiple" : "imageContainer"}>
                      {Array.isArray(step.img)
                        ? step.img.map((src, i) => (
                            <img key={i} src={src} alt={`${step.title} ${i + 1}`} />
                          ))
                        : <img src={step.img} alt={step.title} />
                      }
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
